import {Button} from "../../../Form/Button/Button";
import {buttonColors} from "../../../Pickers/InputTheme";
import React, {useContext, useEffect, useState} from "react";
import {GridReactContext, GridReactContextProvider} from "../../../../Utils/DataGridWrapper/GridContext";
import './RemoveOrgCell.scss'
import {Modal} from "../../../Smart/Modal/Modal";
import {getOrganizationUsersByCount, removeOrganizationAPI} from "../../../../Services/api";
import {AlertDialog} from "../../../Smart/AlertDialog/AlertDialog";
import {get} from "lodash";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import {RemoveOrgModalContent} from "./RemoveOrgModalContent";
import {invalidateQueries, queryKeys} from "../../../Hooks/useQueryHooks";

const tableFilters = {
    showFilters: {
        search: true,
    },
    filterValues: {},
    filterData: {}
}

/**
 * Component that provides a button to remove an organization.
 * Opens a modal that displays users belonging exclusively to the organization
 * and allows bulk deletion before removing the organization itself.
 *
 * @param {object} params - Component parameters
 * @param {string} params.organizationId - The ID of the organization to remove
 * @param {string} params.organizationName - The name of the organization to display
 * @returns {JSX.Element} Button to trigger organization removal with modal
 */
export const RemoveOrgCell = ({organizationId, organizationName}) => {
    const {i18n} = useContext(I18nReactContext);
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [localTableFilters, setLocalTableFilters] = useState()
    const [organizationUsersCount, setOrganizationUsersCount] = useState(0)
    const [showFailedAlert, setShowFailedAlert] = useState(false)
    const [alertMessage, setAlertMessage] = useState('')
    const gridContext = useContext(GridReactContext)

    /**
     * Fetches the current count of users who belong exclusively to this organization.
     */
    const fetchOrganizationUsersCount = async () => {
        try {
            const organizationUsers = await getOrganizationUsersByCount({
                paginationModel: { page: 0, pageSize: 1 }, // Only need count
                filterModel: localTableFilters
            });
            setOrganizationUsersCount(get(organizationUsers, "totalCount", 0));
        } catch (error) {
            console.error('Error fetching organization users count:', error);
            setOrganizationUsersCount(0);
        }
    };

    useEffect(() => {
        setLocalTableFilters({
            ...tableFilters,
            filterValues: {
                ...tableFilters.filterValues,
                organizationId,
                desiredOrganizationCount: 1,
                allUserOrganizations: true
            }
        })
    }, [organizationId]);

    const removeOrg = async () => {
        try {
            await removeOrganizationAPI({organizationId})
            invalidateQueries({queryKeys: [
                queryKeys.adminOrganizations(),
                queryKeys.uniqueSignDocs(false),
                queryKeys.uniqueSignDocs(true)
            ]})
            gridContext.triggerTableRefresh()
            setIsModalOpen(false)
        } catch (error) {
            console.error('Error removing organization:', error);
            setShowFailedAlert(true)
            setAlertMessage(i18n.translate("organizationalUnitTable.removeOrgCell.failedDeleteOrgError"))
        }
    }

    const onOpenModal = async () => {
        if (localTableFilters) {
            await fetchOrganizationUsersCount();
        }
        setIsModalOpen(true)
    }

    const onModalClose = () => {
        setIsModalOpen(false)
        setOrganizationUsersCount(0)
    }

    const handleUserDeleted = async () => {
        // Refresh user count after deletion
        await fetchOrganizationUsersCount();
    };

    const clearAndCloseAlerts = () => {
        setShowFailedAlert(false)
        setAlertMessage('')
    }

    return (
        <div className="removeOrgCell">
            <Button
                color={buttonColors.GREY}
                size="small"
                text={i18n.translate('organizationalUnitTable.removeOrgCell.button')}
                onClick={onOpenModal}
            />
            {isModalOpen && localTableFilters && (
                <Modal open={isModalOpen} onClose={onModalClose}>
                    <GridReactContextProvider>
                        <RemoveOrgModalContent
                            organizationId={organizationId}
                            organizationName={organizationName}
                            localTableFilters={localTableFilters}
                            onUserDeleted={handleUserDeleted}
                            onRemoveOrg={removeOrg}
                            onModalClose={onModalClose}
                            organizationUsersCount={organizationUsersCount}
                        />
                    </GridReactContextProvider>
                    {showFailedAlert && (
                        <AlertDialog
                            title={i18n.translate('organizationalUnitTable.removeOrgCell.alertErrorDeleteOrgTitle')}
                            alertMessage={alertMessage}
                            onOk={clearAndCloseAlerts}
                        />
                    )}
                </Modal>
            )}
        </div>
    );
}